import React, { useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpFromBracket } from '@fortawesome/sharp-solid-svg-icons/faArrowUpFromBracket';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import style from './file-upload-button.module.less';
import { validateFile } from './file-validation';
import { messages } from './messages';

/**
 * @param {object} props
 * @param {boolean} [props.isDisabled] - Whether the upload button is disabled
 * @param {(error: string) => void} props.onUploadError - Callback when file upload encounters an error
 * @param {(file: File) => void} props.onFileSelect - Callback when a file is successfully selected
 * @param {string[]} [props.acceptedFileTypes] - Array of file types to accept. A file type can be a file extension (e.g., '.pdf'), a MIME type (e.g., 'application/pdf'), or a general type (e.g., 'image/*')
 * @param {number} [props.maxFileSizeMB] - Maximum file size in megabytes
 * @returns {React.JSX.Element}
 */
export const FileUploadButton = ({ isDisabled, onUploadError, onFileSelect, acceptedFileTypes, maxFileSizeMB }) => {
  const fileInputRef = useRef(null);
  const intl = useIntl();

  const onClickUpload = useCallback(() => {
    if (isDisabled || !fileInputRef.current) {
      return;
    }

    fileInputRef.current.click();
  }, [isDisabled]);

  const onFileChange = useCallback(
    (event) => {
      const file = event.target.files?.[0];

      const validation = validateFile(file, {
        acceptedFileTypes,
        maxFileSizeMB,
      });

      if (!validation.isValid) {
        const errorMessage = intl.formatMessage(validation.messageDescriptor, validation.messageValues);
        onUploadError(errorMessage);
        return;
      }

      onFileSelect(file);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [acceptedFileTypes, maxFileSizeMB, onUploadError, onFileSelect, intl],
  );

  return (
    <>
      <Button className={style['upload-button']} disabled={isDisabled} onClick={onClickUpload} theme={THEME_CONSTANTS.secondary}>
        <FontAwesomeIcon className={style['upload-button-icon']} icon={faArrowUpFromBracket} />
        <span className={style['upload-button-text']}>
          <FormattedMessage {...messages.upload} />
        </span>
      </Button>
      <input
        ref={fileInputRef}
        accept={acceptedFileTypes?.join(',')}
        className={style['hidden-file-input']}
        onChange={onFileChange}
        type='file'
      />
    </>
  );
};
